/**
 * deskMesh – the table the lesson's 3D models stand on.
 *
 * It used to be a translucent blue slab with glowing cyan edges, 5 cm thick and
 * centred on the desk height, so every model sank 2.5 cm into it and nothing
 * about it said "table". This builds a real piece of furniture instead:
 *
 *   - a top whose upper surface is exactly at the desk height, so a model's feet
 *     rest ON it (placeAssetOnDock puts the bottom at `deskY`);
 *   - a rounded edge, an apron frame and four legs down to the floor, which is
 *     what makes an object read as grounded rather than floating in the sky;
 *   - a top that receives the models' shadows, the strongest cue that they are
 *     sitting on it.
 *
 * Two finishes: varnished oak for the classroom presets, and the charcoal epoxy
 * resin top on metal legs a school lab bench has. Textures are drawn on a
 * canvas once and shared, so there is nothing to download and nothing to leak
 * when the desk is rebuilt on every re-layout.
 */

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

/** Desk top and frame finish; roomTheme.ts picks one per lesson theme. */
export type DeskFinish = 'wood' | 'lab' | 'stone' | 'aged' | 'walnut' | 'maple' | 'pastel' | 'laminate';

export interface DeskSpec {
  width: number;
  depth: number;
  /** World height of the top surface. */
  deskY: number;
  /** World height of the floor. */
  groundY: number;
  finish: DeskFinish;
}

export const DESK_TOP_THICKNESS = 0.035;
const APRON_HEIGHT = 0.07;
const APRON_THICKNESS = 0.02;
const LEG_SIZE = 0.045;
/** Legs and apron sit this far in from the top's edge, as on a real table. */
const INSET = 0.045;

/** Shared across rebuilds: generated once, never disposed with a desk. */
const sharedTextures = new Map<string, THREE.Texture>();

function canvasTexture(key: string, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void, w: number, h: number) {
  const cached = sharedTextures.get(key);
  if (cached) return cached;
  // No DOM (tests, SSR): the desk is still built, just untextured.
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  draw(ctx, w, h);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 8;
  sharedTextures.set(key, texture);
  return texture;
}

/** Deterministic noise, so the grain is the same on every device and rebuild. */
function seeded(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

interface WoodPalette {
  base: [string, string, string];
  dark: string; // rgb for dark grain
  light: string; // rgb for light grain
  pore: string;
}

const WOODS: Record<'oak' | 'aged' | 'walnut' | 'maple', WoodPalette> = {
  oak: { base: ['#b58556', '#a9784b', '#b8895a'], dark: '92, 58, 30', light: '222, 184, 135', pore: '70, 42, 20' },
  // Old, dark, oiled wood for History.
  aged: { base: ['#5e3d24', '#533521', '#624128'], dark: '30, 18, 10', light: '140, 100, 64', pore: '25, 15, 8' },
  walnut: { base: ['#6b4631', '#5f3e2b', '#704a34'], dark: '42, 26, 17', light: '160, 118, 86', pore: '35, 20, 12' },
  // Pale drafting-table maple for Maths.
  maple: { base: ['#e2c9a2', '#d9bf96', '#e6cea8'], dark: '176, 138, 92', light: '248, 232, 205', pore: '150, 115, 75' },
};

/** Quarter-sawn wood: a warm base, long wavy grain lines, a few darker streaks. */
function woodTexture(key: keyof typeof WOODS) {
  const wood = WOODS[key];
  return canvasTexture(
    key,
    (ctx, w, h) => {
      const rand = seeded(7);
      const base = ctx.createLinearGradient(0, 0, 0, h);
      base.addColorStop(0, wood.base[0]);
      base.addColorStop(0.5, wood.base[1]);
      base.addColorStop(1, wood.base[2]);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 170; i += 1) {
        const y0 = rand() * h;
        const amp = 2 + rand() * 6;
        const freq = (1 + rand() * 3) * ((Math.PI * 2) / w);
        const phase = rand() * Math.PI * 2;
        const dark = rand() < 0.7;
        ctx.strokeStyle = dark
          ? `rgba(${wood.dark}, ${0.08 + rand() * 0.18})`
          : `rgba(${wood.light}, ${0.06 + rand() * 0.12})`;
        ctx.lineWidth = 0.6 + rand() * (dark ? 2.2 : 1.4);
        ctx.beginPath();
        for (let x = 0; x <= w; x += 8) {
          const y = y0 + Math.sin(x * freq + phase) * amp + Math.sin(x * freq * 3.1 + phase) * amp * 0.25;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      // Fine pores.
      for (let i = 0; i < 2600; i += 1) {
        ctx.fillStyle = `rgba(${wood.pore}, ${0.05 + rand() * 0.08})`;
        ctx.fillRect(rand() * w, rand() * h, 1 + rand() * 3, 1);
      }
    },
    1024,
    512
  );
}

/** Polished granite for Geography: grey base, dense black, white and pink grains. */
function graniteTexture() {
  return canvasTexture(
    'granite',
    (ctx, w, h) => {
      const rand = seeded(13);
      ctx.fillStyle = '#8d8a86';
      ctx.fillRect(0, 0, w, h);
      const grains = ['rgba(30,30,32,0.75)', 'rgba(235,232,228,0.6)', 'rgba(170,120,110,0.55)', 'rgba(90,88,86,0.6)'];
      for (let i = 0; i < 9000; i += 1) {
        ctx.fillStyle = grains[Math.floor(rand() * grains.length)];
        const size = 1 + rand() * 4;
        ctx.beginPath();
        ctx.ellipse(rand() * w, rand() * h, size, size * (0.5 + rand() * 0.5), rand() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
    },
    512,
    512
  );
}

/** Epoxy resin lab top: near-black charcoal with a faint mottle. */
function resinTexture() {
  return canvasTexture(
    'resin',
    (ctx, w, h) => {
      const rand = seeded(11);
      ctx.fillStyle = '#23272b';
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 900; i += 1) {
        const r = 6 + rand() * 30;
        const g = ctx.createRadialGradient(rand() * w, rand() * h, 0, rand() * w, rand() * h, r);
        const shade = rand() < 0.5 ? '255,255,255' : '0,0,0';
        g.addColorStop(0, `rgba(${shade},${0.015 + rand() * 0.02})`);
        g.addColorStop(1, `rgba(${shade},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }
    },
    512,
    512
  );
}

function materials(finish: DeskFinish, width: number, depth: number) {
  const tile = (map: THREE.Texture | null, metres: number) => {
    // One texture tile covers about `metres` of surface, so a wide desk does not
    // stretch it and a small one does not repeat it visibly.
    if (map) map.repeat.set(Math.max(0.6, width / metres), Math.max(0.3, depth / metres));
    return map;
  };
  const top = (map: THREE.Texture | null, roughness: number, metalness = 0) =>
    new THREE.MeshStandardMaterial({ color: 0xffffff, map, roughness, metalness });
  const frame = (color: number, roughness: number, metalness = 0) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness });

  switch (finish) {
    case 'lab':
      return { top: top(tile(resinTexture(), 0.8), 0.32, 0.05), frame: frame(0x9aa3ab, 0.45, 0.55) };
    case 'stone':
      return { top: top(tile(graniteTexture(), 0.7), 0.35), frame: frame(0x4a4744, 0.6, 0.3) };
    case 'aged':
      return { top: top(tile(woodTexture('aged'), 1.0), 0.75), frame: frame(0x3b2415, 0.8) };
    case 'walnut':
      return { top: top(tile(woodTexture('walnut'), 1.0), 0.5), frame: frame(0x3f2819, 0.65) };
    case 'maple':
      return { top: top(tile(woodTexture('maple'), 1.0), 0.5), frame: frame(0xb89a72, 0.6) };
    case 'pastel':
      // Classes 1–5: light wood on bright, friendly legs.
      return { top: top(tile(woodTexture('maple'), 1.0), 0.55), frame: frame(0x4fa3e0, 0.5) };
    case 'laminate':
      return { top: frame(0x3a3f47, 0.4, 0.1), frame: frame(0x1f2328, 0.5, 0.4) };
    case 'wood':
    default:
      return { top: top(tile(woodTexture('oak'), 1.0), 0.55), frame: frame(0x7a5232, 0.7) };
  }
}

/**
 * The desk, in its own space: the top surface at y = 0 (so the group is placed
 * at `deskY`), facing +Z, centred on the origin.
 */
export function buildDesk(spec: DeskSpec): THREE.Group {
  const { width, depth, finish } = spec;
  const legLength = Math.max(0, spec.deskY - spec.groundY - DESK_TOP_THICKNESS);
  const mats = materials(finish, width, depth);
  const group = new THREE.Group();

  const top = new THREE.Mesh(
    new RoundedBoxGeometry(width, DESK_TOP_THICKNESS, depth, 3, Math.min(0.012, DESK_TOP_THICKNESS / 2 - 1e-3)),
    mats.top
  );
  top.name = 'deskTop';
  top.position.y = -DESK_TOP_THICKNESS / 2;
  top.receiveShadow = true;
  group.add(top);

  const frameY = -DESK_TOP_THICKNESS - APRON_HEIGHT / 2;
  const innerW = Math.max(0.1, width - 2 * INSET);
  const innerD = Math.max(0.1, depth - 2 * INSET);
  const apron = (w: number, d: number, x: number, z: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, APRON_HEIGHT, d), mats.frame);
    m.position.set(x, frameY, z);
    m.castShadow = true;
    group.add(m);
  };
  apron(innerW, APRON_THICKNESS, 0, innerD / 2 - APRON_THICKNESS / 2);
  apron(innerW, APRON_THICKNESS, 0, -innerD / 2 + APRON_THICKNESS / 2);
  apron(APRON_THICKNESS, innerD, innerW / 2 - APRON_THICKNESS / 2, 0);
  apron(APRON_THICKNESS, innerD, -innerW / 2 + APRON_THICKNESS / 2, 0);

  if (legLength > 0.05) {
    const legGeometry =
      finish === 'lab' || finish === 'laminate' || finish === 'pastel'
        ? new THREE.CylinderGeometry(LEG_SIZE / 2, LEG_SIZE / 2, legLength, 16)
        : new THREE.BoxGeometry(LEG_SIZE, legLength, LEG_SIZE);
    const lx = innerW / 2 - LEG_SIZE / 2;
    const lz = innerD / 2 - LEG_SIZE / 2;
    for (const [x, z] of [[lx, lz], [-lx, lz], [lx, -lz], [-lx, -lz]] as const) {
      const leg = new THREE.Mesh(legGeometry, mats.frame);
      leg.position.set(x, -DESK_TOP_THICKNESS - legLength / 2, z);
      leg.castShadow = true;
      group.add(leg);
    }
  }

  return group;
}

/** Free a desk's geometry and materials. Shared textures stay for the next one. */
export function disposeDesk(object: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const mats = new Set<THREE.Material>();
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    geometries.add(mesh.geometry);
    (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach((m) => mats.add(m));
  });
  geometries.forEach((g) => g.dispose());
  mats.forEach((m) => m.dispose());
}
